import {
  Car,
  CarFront,
  IndianRupee,
  Wallet,
  BadgePercent,
  Clock3,
  ShieldCheck,
  CircleDollarSign,
  RefreshCcw,
  CheckCircle2,
  Banknote,
  KeyRound,
  User,
  Briefcase,
  Users,
  Building2,
  Landmark,
  FileText,
} from "./icons";

import { EMPLOYMENT_TYPES } from "../../constants/employmentTypes";

import {
  process,
  whyMoneyMartItems,
  ctaFeatures,
  leadFormHighlights,
} from "./shared";

import {
  websiteSchema,
  organizationSchema,
  financialServiceSchema,
  localBusinessSchema,
  breadcrumbSchema,
} from "@/components/common/schema";

const schemas = [
  websiteSchema(),
  organizationSchema(),
  financialServiceSchema(),
  localBusinessSchema(),
  breadcrumbSchema([
    {
      name: "Home",
      path: "/",
    },
    {
      name: "Car Loan",
      path: "/car-loan",
    },
  ]),
];

/* -------------------------------------------------------------------------- */
/*                                    SEO                                     */
/* -------------------------------------------------------------------------- */

export const seo = {
  title: "Car Loan | New & Used Car Finance | Money Mart",

  description:
    "Get a Car Loan from leading Banks & NBFCs with competitive interest rates, high funding, flexible EMIs and quick approvals. Finance new or used cars with expert assistance from Money Mart.",

  keywords:
    "Car Loan, New Car Loan, Used Car Loan, Vehicle Loan, Auto Loan, Car Finance, Money Mart",

  canonical: "/car-loan",

  structuredData: { schemas },
};

/* -------------------------------------------------------------------------- */
/*                                    Hero                                    */
/* -------------------------------------------------------------------------- */

export const hero = {
  badge: "Vehicle Finance",

  title: "Drive Home Your Dream Car with Easy Financing",

  description:
    "Whether you're buying a new car or a pre-owned vehicle, Money Mart helps you compare Car Loan offers from leading Banks & NBFCs with competitive interest rates, flexible repayment options and faster approvals.",

  highlights: [
    "Finance for New & Used Cars",
    "Competitive Interest Rates",
    "Flexible EMI Options",
    "Quick Loan Processing",
  ],

  image: "/images/loans/car_loan.webp",

  primaryButtonText: "Get Free Consultation",

  secondaryButtonText: "Check Eligibility",
};

/* -------------------------------------------------------------------------- */
/*                                  Overview                                  */
/* -------------------------------------------------------------------------- */

export const overview = {
  badge: "Car Loans",

  title: "Simple & Affordable Vehicle Financing",

  description:
    "A Car Loan helps you purchase your desired vehicle without paying the entire amount upfront. With flexible repayment tenures and attractive interest rates, you can own your dream car while comfortably managing your monthly finances.",

  items: [
    {
      icon: CarFront,
      title: "New Car Finance",
      description:
        "Finance brand-new cars from leading manufacturers with attractive loan options.",
    },
    {
      icon: Car,
      title: "Used Car Finance",
      description:
        "Purchase certified pre-owned vehicles with flexible financing solutions.",
    },
    {
      icon: Wallet,
      title: "Flexible Repayment",
      description:
        "Choose convenient repayment tenures and EMI options that suit your budget.",
    },
    {
      icon: KeyRound,
      title: "Easy Ownership",
      description:
        "Drive your dream car home without paying the entire purchase price upfront.",
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                  Benefits                                  */
/* -------------------------------------------------------------------------- */

export const benefits = {
  badge: "Benefits",

  title: "Why Choose a Car Loan?",

  description:
    "Car Loans make vehicle ownership affordable by offering convenient financing with competitive interest rates and flexible repayment options.",

  items: [
    {
      icon: CircleDollarSign,
      title: "High Loan Amount",
      description:
        "Finance a significant portion of your vehicle's on-road price, subject to lender policy.",
    },
    {
      icon: BadgePercent,
      title: "Competitive Interest Rates",
      description:
        "Benefit from attractive interest rates offered by leading Banks & NBFCs.",
    },
    {
      icon: Wallet,
      title: "Flexible EMIs",
      description:
        "Choose repayment options and loan tenures that fit your financial goals.",
    },
    {
      icon: Clock3,
      title: "Quick Processing",
      description:
        "Enjoy faster approvals and hassle-free documentation with expert guidance.",
    },
    {
      icon: ShieldCheck,
      title: "Simple Documentation",
      description:
        "Most lenders require only standard KYC, income and vehicle-related documents.",
    },
    {
      icon: CheckCircle2,
      title: "Wide Choice of Lenders",
      description:
        "Compare multiple lenders to find the most suitable financing option for your vehicle purchase.",
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                   Types                                    */
/* -------------------------------------------------------------------------- */

export const types = {
  badge: "Car Loan Solutions",

  title: "Vehicle Finance Solutions We Help Arrange",

  description:
    "Money Mart helps customers compare multiple vehicle finance options from leading Banks & NBFCs based on their requirements.",

  items: [
    {
      icon: CarFront,
      title: "New Car Loan",
      description:
        "Finance the purchase of brand-new passenger vehicles with competitive interest rates and flexible repayment options.",
    },
    {
      icon: Car,
      title: "Used Car Loan",
      description:
        "Finance certified pre-owned vehicles with loan options designed for second-hand cars.",
    },
    {
      icon: RefreshCcw,
      title: "Car Loan Balance Transfer",
      description:
        "Transfer your existing Car Loan to another lender for better interest rates or improved loan terms.",
    },
    {
      icon: Banknote,
      title: "Top-Up on Existing Car Loan",
      description:
        "Eligible borrowers may obtain additional funding over their existing Car Loan, subject to lender policy.",
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                Eligibility                                 */
/* -------------------------------------------------------------------------- */

export const eligibility = {
  badge: "Eligibility",

  title: "Check Your Car Loan Eligibility",

  description:
    "Car Loan eligibility depends on your income, repayment capacity, employment profile, credit history, and the lender's internal policy.",

  requirements: [
    {
      icon: User,
      title: "Applicant",
      value:
        "Salaried Individuals, Self-Employed Professionals & Business Owners",
    },
    {
      icon: Briefcase,
      title: "Employment",
      value: "Stable Employment or Established Business (as per lender norms)",
    },
    {
      icon: IndianRupee,
      title: "Income",
      value: "Regular & Verifiable Source of Income",
    },
    {
      icon: CarFront,
      title: "Vehicle",
      value: "Eligible New or Used Car meeting lender criteria",
    },
  ],

  approvalFactors: [
    "Monthly income and repayment capacity",
    "Credit score & repayment history",
    "Employment or business stability",
    "Vehicle age and valuation (for used cars)",
    "Loan amount and down payment",
    "Existing financial obligations",
  ],

  cta: {
    title: "Want to know your loan eligibility?",

    description:
      "Our experts will assess your profile and help you find the most suitable Car Loan from leading Banks & NBFCs.",

    buttonText: "Check Eligibility Now",
  },
};

/* -------------------------------------------------------------------------- */
/*                                 Documents                                  */
/* -------------------------------------------------------------------------- */

export const documents = {
  badge: "Documents Required",

  title: "Documents Required for a Car Loan",

  description:
    "The exact documentation may vary depending on the lender and applicant profile. These are the documents commonly required for most Car Loan applications.",

  groups: [
    {
      icon: Users,
      title: "Documents Required for All Applicants",

      documents: [
        "Passport Size Photograph",
        "PAN Card",
        "Aadhaar Card",
        "Address Proof",
      ],
    },
    {
      icon: Briefcase,
      title: "Income Documents (Salaried)",

      documents: [
        "Latest Salary Slips",
        "Salary Account Bank Statements",
        "Form 16 (if required)",
        "Employment ID / Offer Letter (if applicable)",
      ],
    },
    {
      icon: Building2,
      title: "Income Documents (Self-Employed)",

      documents: [
        "Last 2–3 Years Income Tax Returns",
        "Balance Sheet & Profit & Loss Statement",
        "Business Proof",
        "Business Bank Statements",
      ],
    },
    {
      icon: Car,
      title: "Vehicle Documents",

      documents: [
        "Vehicle Quotation / Proforma Invoice",
        "Vehicle Registration Details (for Used Car Loan)",
        "Insurance Details (if applicable)",
        "RC Copy (for Balance Transfer or Used Car Loan)",
      ],
    },
    {
      icon: Landmark,
      title: "Banking Documents",

      documents: [
        "Last 6 Months Bank Statements",
        "Existing Loan Details (if applicable)",
      ],
    },
    {
      icon: FileText,
      title: "Additional Supporting Documents",

      documents: [
        "Driving License (if required)",
        "Any Additional Documents Requested by the Lender",
      ],
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                   Process                                  */
/* -------------------------------------------------------------------------- */

export const loanProcess = {
  ...process,

  title: "Get Your Car Loan in 6 Simple Steps",

  description:
    "From eligibility assessment and document verification to lender approval and vehicle delivery, Money Mart supports you throughout the Car Loan journey.",
};

/* -------------------------------------------------------------------------- */
/*                              Why Money Mart                                */
/* -------------------------------------------------------------------------- */

export const whyMoneyMart = {
  badge: "Why Money Mart",

  title: "Why Choose Money Mart for Your Car Loan?",

  description:
    "We compare Car Loan offers from multiple Banks and NBFCs to help you secure competitive interest rates, flexible repayment options and a hassle-free borrowing experience.",

  items: whyMoneyMartItems,
};

/* -------------------------------------------------------------------------- */
/*                                     FAQ                                    */
/* -------------------------------------------------------------------------- */

export const faq = {
  badge: "Frequently Asked Questions",

  title: "Car Loan FAQs",

  description:
    "Find answers to the most commonly asked questions about Car Loans.",

  items: [
    {
      question: "Can I get a loan for a used car?",

      answer:
        "Yes. Most Banks and NBFCs offer financing for eligible pre-owned vehicles. The loan amount depends on factors such as the vehicle's age, condition, valuation, and the lender's policy.",
    },
    {
      question: "How much funding can I get for a Car Loan?",

      answer:
        "Many lenders finance a substantial portion of the vehicle's on-road price for new cars. The exact loan amount depends on the lender's policy, your eligibility, and the vehicle being financed.",
    },
    {
      question: "Can I transfer my existing Car Loan to another bank?",

      answer:
        "Yes. Eligible borrowers can opt for a Car Loan Balance Transfer to another lender for better interest rates, improved repayment terms, or additional benefits, subject to lender approval.",
    },
    {
      question: "Can I get a top-up on my existing Car Loan?",

      answer:
        "Some lenders allow eligible borrowers to obtain additional funding over an existing Car Loan through a Top-Up facility, subject to repayment history and lender policy.",
    },
    {
      question: "What factors affect Car Loan approval?",

      answer:
        "Approval depends on your income, repayment capacity, credit score, employment or business stability, existing financial obligations, and the lender's credit assessment.",
    },
    {
      question: "How long does Car Loan approval take?",

      answer:
        "Approval timelines vary by lender and documentation. Applications with complete documentation generally receive faster processing.",
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                     CTA                                    */
/* -------------------------------------------------------------------------- */

export const cta = {
  badge: "Ready to Buy Your Dream Car?",

  title: "Compare Car Loan Offers from Leading Banks & NBFCs",

  description:
    "Let Money Mart help you secure the right Car Loan with competitive interest rates, flexible EMIs, and expert guidance throughout the process.",

  features: ctaFeatures,

  primaryButton: {
    text: "Apply for Car Loan",
  },

  secondaryButton: {
    text: "Call +91 92144 17434",
    href: "tel:+919214417434",
  },
};

/* -------------------------------------------------------------------------- */
/*                                 Lead Form                                  */
/* -------------------------------------------------------------------------- */

export const leadForm = {
  badge: "Free Car Loan Consultation",

  title: "Speak to a Car Loan Expert",

  description:
    "Our advisors will compare Car Loan offers from multiple Banks & NBFCs and help you choose the most suitable financing option for your vehicle.",

  highlights: leadFormHighlights,

  phone: {
    label: "Call us directly",
    text: "+91 92144 17434",
    href: "tel:+919214417434",
  },

  form: {
    title: "Get Free Consultation",

    description:
      "Fill in your details and our Car Loan expert will contact you shortly.",

    placeholders: {
      name: "Full Name *",
      mobile: "Mobile Number *",
      employment: "Employment Type",
      income: "Monthly Income",
      loanAmount: "Required Loan Amount",
    },

    incomeField: "monthlyIncome",

    employmentOptions: EMPLOYMENT_TYPES,

    incomeOptions: [
      {
        value: "below-25k",
        label: "Below ₹25,000",
      },
      {
        value: "25k-50k",
        label: "₹25,000 – ₹50,000",
      },
      {
        value: "50k-100k",
        label: "₹50,000 – ₹1 Lakh",
      },
      {
        value: "100k-200k",
        label: "₹1 Lakh – ₹2 Lakhs",
      },
      {
        value: "200k-plus",
        label: "Above ₹2 Lakhs",
      },
    ],

    submitButton: "Get Free Consultation",

    privacyText:
      "Your information is secure and will only be used to assist with your Car Loan enquiry. No spam. No hidden charges.",
  },
};

/* -------------------------------------------------------------------------- */
/*                                  Export                                    */
/* -------------------------------------------------------------------------- */

const carLoan = {
  seo,
  hero,
  overview,
  benefits,
  types,
  eligibility,
  documents,
  process: loanProcess,
  whyMoneyMart,
  faq,
  cta,
  leadForm,
};

export default carLoan;
